/**
 * Accessory Request History Section Component
 * แสดง **ประวัติเดิม** ของการขออุปกรณ์เสริมในสัญญาเช่า
 *
 * โมดูล `accessory_requests` ถูกยุบเข้า "ใบเบิก-ยืมอุปกรณ์" แล้ว (PO 2026-09-07)
 * — การเบิกใหม่ทั้งหมดทำผ่าน `RentalStockIssueSection` ที่อยู่เหนือการ์ดนี้
 * การ์ดนี้จึงเหลือหน้าที่เดียวคือให้ข้อมูลย้อนหลังที่เคยบันทึกไว้ยังอ่านได้
 * และ **ซ่อนตัวเองเมื่อไม่มีประวัติ** เพื่อไม่ให้หน้ารายละเอียดมีการ์ดว่างของโมดูลที่เลิกใช้แล้ว
 *
 * รองรับการแสดงผล 3 ประเภท:
 * - REQUEST_NEW: เบิกใหม่
 * - REPLACE: เปลี่ยน
 * - PURCHASE: ขอซื้อ
 */
import React, { useState, useEffect } from 'react';
import { RiToolsLine, RiAddLine, RiRefreshLine, RiShoppingCart2Line, RiTimeLine, RiUser3Line } from 'react-icons/ri';
import accessoryRequestService, {
  AccessoryRequestResponse,
  REQUEST_TYPES,
  ACCESSORY_CONDITIONS,
} from '../../services/accessoryRequestService';
import { usePermission } from '../../hooks/usePermission';

interface AccessoryRequestHistorySectionProps {
  rentalId: number;
}

// Request type badge config
const REQUEST_TYPE_BADGES: { [key: string]: { label: string; color: string; bgColor: string } } = {
  REQUEST_NEW: { label: 'เบิกใหม่', color: 'text-info-700', bgColor: 'bg-info-50 border-info-200' },
  REPLACE: { label: 'เปลี่ยน', color: 'text-warning-700', bgColor: 'bg-warning-50 border-warning-200' },
  PURCHASE: { label: 'ขอซื้อ', color: 'text-success-700', bgColor: 'bg-success-50 border-success-200' },
};

// Condition labels
const CONDITION_LABELS: { [key: string]: { label: string; color: string } } = {
  DAMAGED: { label: 'ชำรุด', color: 'bg-danger-100 text-danger-800' },
  LOST: { label: 'สูญหาย', color: 'bg-warning-100 text-warning-800' },
  WORN_OUT: { label: 'สึกหรอ', color: 'bg-warning-100 text-warning-800' },
};

// Icon component mapping
const RequestTypeIcon: React.FC<{ type: string; className?: string }> = ({ type, className = 'w-4 h-4' }) => {
  switch (type) {
    case 'REQUEST_NEW':
      return <RiAddLine className={className} />;
    case 'REPLACE':
      return <RiRefreshLine className={className} />;
    case 'PURCHASE':
      return <RiShoppingCart2Line className={className} />;
    default:
      return <RiToolsLine className={className} />;
  }
};

const AccessoryRequestHistorySection: React.FC<AccessoryRequestHistorySectionProps> = ({
  rentalId,
}) => {
  const { hasPermission } = usePermission();

  const [history, setHistory] = useState<AccessoryRequestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // G-27: backend GET ต้องมี accessory_requests.view — ไม่มีสิทธิ์ก็ไม่ fetch (กัน 403)
    if (!hasPermission('accessory_requests.view')) return;
    loadHistory();
  }, [rentalId]);

  const loadHistory = async () => {
    if (!rentalId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await accessoryRequestService.getRentalHistory(rentalId);
      setHistory(data.items || []);
    } catch (err) {
      console.error('Error loading accessory request history:', err);
      setError('ไม่สามารถโหลดประวัติการขออุปกรณ์เสริม');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ไม่มีสิทธิ์ดูประวัติ → ไม่ render section เลย
  if (!hasPermission('accessory_requests.view')) {
    return null;
  }

  // โมดูลเลิกใช้แล้ว — ระหว่างโหลดไม่ต้องจองที่บนหน้าจอ (ส่วนใหญ่จะไม่มีประวัติเลย)
  if (loading) {
    return null;
  }

  // โหลดไม่ได้ = ไม่มีอะไรให้แสดง (ข้อมูลย้อนหลังอย่างเดียว ไม่ใช่ข้อมูลที่ผู้ใช้ต้องใช้ทำงานต่อ)
  if (error) {
    return null;
  }

  // ไม่มีประวัติ = ไม่ต้องมีการ์ดของโมดูลที่ถูกยุบไปแล้ว
  if (history.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-secondary-900 flex items-center">
            <RiToolsLine className="w-5 h-5 mr-2 text-secondary-500" />
            ประวัติการขออุปกรณ์เสริม (ข้อมูลเดิม)
            <span className="ml-2 bg-secondary-100 text-secondary-600 text-sm px-2 py-0.5 rounded-full">
              {history.length}
            </span>
          </h2>
          <span className="text-xs text-secondary-500">
            โมดูลนี้ถูกยุบเข้า &quot;ใบเบิกอุปกรณ์&quot; แล้ว — เบิกใหม่ที่การ์ดด้านบน
          </span>
        </div>
        <div className="p-6">
          {(
            <div className="space-y-4">
              {history.map((item) => {
                const typeBadge = REQUEST_TYPE_BADGES[item.request_type] || REQUEST_TYPE_BADGES.REQUEST_NEW;
                const conditionInfo = item.old_condition ? CONDITION_LABELS[item.old_condition] : null;

                return (
                  <div
                    key={item.id}
                    className="border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors"
                  >
                    {/* Header: Date, Request Type Badge & Condition */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center text-sm text-secondary-500">
                          <RiTimeLine className="w-4 h-4 mr-1" />
                          {formatDate(item.request_date)}
                        </div>
                        {/* Request Type Badge */}
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${typeBadge.bgColor} ${typeBadge.color}`}
                        >
                          <RequestTypeIcon type={item.request_type} className="w-3.5 h-3.5 mr-1" />
                          {typeBadge.label}
                        </span>
                      </div>

                      {/* Condition Badge (for REPLACE only) */}
                      {conditionInfo && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${conditionInfo.color}`}
                        >
                          {conditionInfo.label}
                        </span>
                      )}
                    </div>

                    {/* Accessory Details */}
                    <div className="flex items-center mb-3">
                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">อุปกรณ์เสริม</p>
                        <p className="font-medium text-secondary-900">
                          {item.device?.name || 'ไม่ระบุ'}
                        </p>
                        {item.device?.code && (
                          <p className="text-xs text-secondary-500">รหัส: {item.device.code}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-secondary-500">จำนวน</p>
                        <p className="font-medium text-secondary-900">
                          {item.quantity} {item.device?.unit || 'ชิ้น'}
                        </p>
                      </div>
                    </div>

                    {/* Reason */}
                    <div className="mb-2">
                      <p className="text-sm text-secondary-500">เหตุผล:</p>
                      <p className="text-sm text-secondary-700">{item.reason}</p>
                    </div>

                    {/* Notes */}
                    {item.notes && (
                      <div className="mb-2">
                        <p className="text-sm text-secondary-500">หมายเหตุ:</p>
                        <p className="text-sm text-secondary-600 italic">{item.notes}</p>
                      </div>
                    )}

                    {/* Requested By */}
                    <div className="text-xs text-secondary-400 mt-2 pt-2 border-t border-secondary-100 flex items-center">
                      <RiUser3Line className="w-3.5 h-3.5 mr-1" />
                      โดย: {item.requested_by?.full_name || item.requested_by?.username || 'ไม่ระบุ'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccessoryRequestHistorySection;
